const UNSAFE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

/** Same contract as the user SPA's client: relative paths, cookie auth, CSRF echo. */
export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);

  if (UNSAFE_METHODS.includes(method)) {
    const token = getCookie('XSRF-TOKEN');
    if (token) {
      headers.set('X-XSRF-TOKEN', decodeURIComponent(token));
    }

    // A FormData body must be left alone. The browser writes its own Content-Type for it, and
    // that header carries the multipart boundary — the string that tells the server where one
    // field ends and the next begins. Setting application/json here, as this used to do for any
    // body at all, sends a multipart payload the server is told to parse as JSON, and an image
    // upload fails with an error that mentions neither images nor uploads.
    if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const response = await fetch(path, { ...options, method, headers, credentials: 'include' });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  // Errors arrive as application/problem+json, which is JSON too.
  const isJson = contentType.includes('json');
  const payload = isJson ? await response.json() : undefined;

  if (!response.ok) {
    throw new ApiError(response.status, payload?.title ?? response.statusText, payload?.detail);
  }

  return payload as T;
}
